import { Container, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner } from "reactstrap";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import useToast from "../../Components/Hooks/useToast";
import DeleteDailog from "../../Components/Common/DeleteDailog";
import { fetchAllPosts, createPost, updatePost, deletePost, getPostById, bulkDeletePosts } from "../../slices/thunks";
import { getPostColumns } from "../../Components/Common/columns/Posts";

const Posts = () => {
    const dispatch = useDispatch();
    const [selectedRows, setSelectedRows] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const posts = useSelector((state) => state.Posts?.posts || []);
    const totalRows = useSelector((state) => state.Posts?.totalRows || 0);
    const loading = useSelector((state) => state.Posts?.loading || false);

    const [filterParams, setFilterParams] = useState({
        page: 1,
        perPage: 10,
        limit: 10,
        search: "",
        orderBy: "createdAt",
        order: "desc",
    });

    const initialFormState = {
        _id: "",
        description: "",
        mediaType: "image",
        media: "",
        updatedAt: null,
    };

    const [form, setForm] = useState(initialFormState);
    const [previewImage, setPreviewImage] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [modalLoading, setModalLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(null);

    const showToastMessage = useToast();

    const uploadFileToServer = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}api/upload`, {
                method: "POST",
                body: formData,
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            return await response.json();
        } catch (error) {
            throw new Error(`Upload failed: ${error.message}`);
        }
    };

    const toastFallbacks = {
        success: {
            create: "Post created successfully!",
            update: "Post updated successfully!",
            delete: "Post deleted successfully!",
            bulkDelete: "Selected posts deleted successfully!",
            upload: "Image uploaded successfully!"
        },
        error: {
            create: "Failed to create post. Please try again.",
            update: "Failed to update post. Please try again.",
            delete: "Failed to delete post. Please try again.",
            fetch: "Failed to load posts. Please try again.",
            fileSize: "File size too large! Maximum 5MB allowed.",
            fileType: "Invalid file type! Only images are allowed.",
            bulkDelete: "Failed to delete posts. Please try again.",
            upload: "Failed to upload image. Please try again.",
            noSelection: "Please select at least one post to delete.",
        }
    };

    const fetchData = async (params = {}) => {
        const updatedParams = {
            ...filterParams,
            ...params,
            limit: params.perPage || params.rowsPerPage || filterParams.limit,
            page: params.page || params.currentPage || filterParams.page,
        };
        setFilterParams(updatedParams);
        await dispatch(fetchAllPosts(updatedParams));
    };

    useEffect(() => { 
        fetchData(); 
    }, [dispatch]);

    const handleRowSelected = (rows) => { 
        setSelectedRows(rows); 
    };

    const handleAddPost = () => {
        setIsEdit(false);
        setForm(initialFormState);
        setPreviewImage("");
        setModalOpen(true);
    };

    const buildMediaUrl = useMemo(() => {
        return (path, cacheKey) => {
            if (!path) return "";
            const cacheSuffix = cacheKey ? `?v=${cacheKey}` : `?v=${Date.now()}`;
            if (/^https?:\/\//i.test(path)) {
                return `${path}${path.includes("?") ? "&" : "?"}${cacheSuffix.slice(1)}`;
            }
            const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
            const baseUrl = process.env.REACT_APP_API_URL || "";
            return `${baseUrl}${normalizedPath}${cacheSuffix}`;
        };
    }, []);

    const handleEditPost = async (row) => {
        setIsEdit(true);
        setModalOpen(true);
        setModalLoading(true);

        try {
            const res = await dispatch(getPostById(row._id));
            if (res?.error) {
                showToastMessage("error", res?.message || toastFallbacks.error.fetch);
                setForm({
                    _id: row._id,
                    description: row.description || "",
                    mediaType: row.mediaType || "image",
                    media: row.mediaUrl || "",
                    updatedAt: row.updatedAt || row.modifiedAt || row.createdAt || Date.now(),
                });
                setPreviewImage(row.mediaUrl ? buildMediaUrl(row.mediaUrl, row.updatedAt || row.modifiedAt || Date.now()) : "");
                return;
            }
            const data = res?.payload?.data || row;
            const updatedAt = data.updatedAt || data.modifiedAt || data.createdAt || Date.now();
            setForm({
                _id: data._id,
                description: data.description,
                mediaType: data.mediaType || "image",
                media: data.mediaUrl || "",
                updatedAt: updatedAt,
            });
            if (data.mediaUrl) {
                setPreviewImage(buildMediaUrl(data.mediaUrl, updatedAt));
            }
        } catch (error) {
            showToastMessage("error", error?.message || toastFallbacks.error.fetch);
            setForm({
                _id: row._id,
                description: row.description || "",
                mediaType: row.mediaType || "image",
                media: row.mediaUrl || "",
                updatedAt: row.updatedAt || row.modifiedAt || row.createdAt || Date.now(),
            });
            setPreviewImage(row.mediaUrl ? buildMediaUrl(row.mediaUrl, row.updatedAt || row.modifiedAt || Date.now()) : "");
        } finally {
            setModalLoading(false);
        }
    };

    const handleViewPost = async (row) => {
        try {
            const res = await dispatch(getPostById(row._id));
            if (res?.error) {
                setViewData(row);
            } else {
                setViewData(res?.payload?.data || row);
            }
        } catch (error) {
            setViewData(row);
        }
        setViewModalOpen(true);
    };

    const handleDeletePost = async (event, id) => {
        event.preventDefault();
        event.stopPropagation();

        setDeleteLoading(id);

        try {
            const result = await dispatch(deletePost(id));
            if (result?.ok) {
                showToastMessage(
                    "success",
                    result?.message || toastFallbacks.success.delete
                );
                fetchData();
            } else {
                showToastMessage(
                    "error",
                    result?.message || toastFallbacks.error.delete
                );
            }
        } catch (error) {
            showToastMessage(
                "error",
                error?.message || toastFallbacks.error.delete
            );
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleBulkDeleteClick = () => {
        if (selectedRows.length === 0) {
            showToastMessage("error", toastFallbacks.error.noSelection);
            return;
        }
        setConfirmMessage(`Are you sure you want to delete ${selectedRows.length} posts?`);
        setConfirmAction(() => handleBulkDelete);
        setShowConfirmPopup(true);
    };

    const handleBulkDelete = async () => {
        setIsBulkDeleting(true);
        const ids = selectedRows.map(row => row._id);
        try {
            const result = await dispatch(bulkDeletePosts(ids));
            if (result?.ok || result?.payload?.ok) {
                const message = result?.message || toastFallbacks.success.bulkDelete;
                const toastType = message.includes("No posts found") || message.includes("already deleted") ? "info" : "success";
                showToastMessage(toastType, message);
                setSelectedRows([]);
                await fetchData();
            } else {
                showToastMessage("error", result?.message || toastFallbacks.error.bulkDelete);
            }
        } catch (error) {
            showToastMessage("error", error?.message || toastFallbacks.error.bulkDelete);
        } finally {
            setIsBulkDeleting(false);
            setShowConfirmPopup(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.description.trim()) {
            showToastMessage("error", "Description is required!");
            return;
        }
        setIsSubmitting(true);
        try {
            let payload;
            if (form.media instanceof File) {
                if (isEdit) {
                    const formData = new FormData();
                    formData.append("description", form.description);
                    formData.append("mediaType", form.mediaType);
                    formData.append("media", form.media);
                    payload = formData;
                } else {
                    const uploadResponse = await uploadFileToServer(form.media);
                    if (uploadResponse.status === 1 && uploadResponse.data?.fileUrl) {
                        payload = {
                            description: form.description,
                            mediaType: form.mediaType,
                            mediaUrl: uploadResponse.data.fileUrl,
                        };
                        showToastMessage("success", uploadResponse.message || toastFallbacks.success.upload);
                    } else {
                        throw new Error(uploadResponse.message || "File upload failed");
                    }
                }
            } else {
                payload = {
                    description: form.description,
                    mediaType: form.mediaType,
                    mediaUrl: typeof form.media === "string" ? form.media : "",
                };
            }
            const res = isEdit ? await dispatch(updatePost(form._id, payload)) : await dispatch(createPost(payload));
            if (res?.ok) {
                const fallback = isEdit ? toastFallbacks.success.update : toastFallbacks.success.create;
                showToastMessage("success", res?.message || fallback);
                setModalOpen(false);
                setForm(initialFormState);
                setPreviewImage("");
                await fetchData();
            } else {
                const errorMsg = res?.message || (isEdit ? toastFallbacks.error.update : toastFallbacks.error.create);
                showToastMessage("error", errorMsg);
            }
        } catch (err) {
            showToastMessage("error", err?.message || (isEdit ? toastFallbacks.error.update : toastFallbacks.error.create));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            showToastMessage("error", toastFallbacks.error.fileSize);
            e.target.value = "";
            return;
        }
        const allowedExtensions = ["png", "jpg", "jpeg"];
        const extension = file.name?.split(".").pop()?.toLowerCase();
        const mimeAllowed = ["image/png", "image/jpg", "image/jpeg"];
        if (!allowedExtensions.includes(extension) || !mimeAllowed.includes(file.type)) {
            showToastMessage("error", toastFallbacks.error.fileType);
            e.target.value = "";
            return;
        }
        setForm({ ...form, media: file, mediaType: "image", updatedAt: Date.now() });
        setPreviewImage(URL.createObjectURL(file));
    };

    const handleModalClose = () => {
        if (!modalLoading && !isSubmitting) {
            setModalOpen(false);
            setForm(initialFormState);
            setPreviewImage("");
        }
    };

    const handleViewModalClose = () => {
        setViewModalOpen(false);
    };

    const columns = getPostColumns({ 
        onEditClick: handleEditPost, 
        onDeleteClick: handleDeletePost, 
        onViewClick: handleViewPost,
        deleteLoading: deleteLoading,
    });

    const resolvedPreviewImage = useMemo(() => {
        if (previewImage) return previewImage;
        if (typeof form.media === "string" && form.media) {
            return buildMediaUrl(form.media, form.updatedAt || Date.now());
        }
        return "";
    }, [previewImage, form.media, form.updatedAt, buildMediaUrl]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Posts" pageTitle="Posts" />
                    <CommonDataTable
                        onRowClicked={handleViewPost}
                        pointerOnHover
                        highlightOnHover
                        columns={columns}
                        data={posts}
                        totalRows={totalRows}
                        loading={loading}
                        fetchData={fetchData}
                        filterParams={filterParams}
                        searchEnable
                        checkboxEnabled={true}
                        showAddButton={true}
                        buttonName="Add Post"
                        addOnClickMethod={handleAddPost}
                        showExportButton={true}
                        exportFileName="posts"
                        showBulkDeleteButton={true}
                        bulkDelete={handleBulkDeleteClick}
                        selectedRows={selectedRows}
                        bulkSelected={handleRowSelected}
                        isBulkDeleting={isBulkDeleting}
                    />

                    <Modal isOpen={modalOpen} toggle={handleModalClose}>
                        <ModalHeader 
                            toggle={!modalLoading && !isSubmitting ? handleModalClose : undefined} 
                            className="modal-title text-body fw-semibold modal-fixed-header-footer"
                        >
                            {isEdit ? "Edit Post" : "Add Post"}
                        </ModalHeader>
                        <Form onSubmit={handleSubmit}>
                            <ModalBody>
                                <FormGroup>
                                    <Label for="postDescription">Description *</Label>
                                    <Input 
                                        id="postDescription" 
                                        type="textarea" 
                                        value={form.description} 
                                        onChange={(e) => setForm({ ...form, description: e.target.value })} 
                                        placeholder="Enter post description..." 
                                        required 
                                        disabled={modalLoading || isSubmitting}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="postImage">Upload Image</Label>
                                    <Input 
                                        id="postImage" 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handleFileChange} 
                                        disabled={modalLoading || isSubmitting}
                                    />
                                    <small className="text-muted">Maximum file size: 5MB. Supported formats: JPG, PNG, GIF</small>
                                    {resolvedPreviewImage && (
                                        <div className="mt-2">
                                            <img src={resolvedPreviewImage} alt="Preview" style={{ width: "120px", height: "120px", borderRadius: "8px", border: "1px solid #ccc", objectFit: "cover" }} />
                                        </div>
                                    )}
                                </FormGroup>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="secondary" onClick={handleModalClose} disabled={modalLoading || isSubmitting}>
                                    Cancel
                                </Button>
                                <Button color="primary" type="submit" disabled={modalLoading || isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            {isEdit ? "Updating..." : "Saving..."}
                                        </>
                                    ) : (
                                        isEdit ? "Update Post" : "Create Post"
                                    )}
                                </Button>
                            </ModalFooter>
                        </Form>
                    </Modal>

                    <Modal isOpen={viewModalOpen} toggle={handleViewModalClose} size="md">
                        <ModalHeader toggle={handleViewModalClose} className="modal-title text-body fw-semibold modal-fixed-header-footer">
                            Post Details
                        </ModalHeader>
                        <ModalBody>
                            {viewData && (
                                <div className="view-post-content">
                                    <div className="text-center mb-4">
                                        {viewData.mediaUrl && (
                                            <img src={buildMediaUrl(viewData.mediaUrl, viewData.updatedAt || viewData.modifiedAt || Date.now())} alt="Post" style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "contain", borderRadius: "8px", border: "1px solid #ddd" }} />
                                        )}
                                    </div>
                                    <div className="post-details">
                                        <div className="mb-3">
                                            <strong className="text-muted">Description:</strong>
                                            <p className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{viewData.description}</p>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted">Created:</strong>
                                            <p className="mt-1">{viewData.createdAt ? new Date(viewData.createdAt).toLocaleString() : "-"}</p>
                                        </div>
                                        {viewData.author && (
                                            <div className="mb-3">
                                                <strong className="text-muted">Author:</strong>
                                                <p className="mt-1">{viewData.author.firstname} {viewData.author.lastname}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={handleViewModalClose}>Close</Button>
                        </ModalFooter>
                    </Modal>

                    {showConfirmPopup && confirmAction && (
                        <DeleteDailog
                            isOpen={showConfirmPopup}
                            title="Confirm Bulk Deletion"
                            message={confirmMessage}
                            confirmLabel={isBulkDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Deleting...
                                </>
                            ) : "Yes, Delete All"}
                            cancelLabel="Cancel"
                            confirmColor="danger"
                            onConfirm={() => {
                                confirmAction();
                            }}
                            onCancel={() => {
                                if (!isBulkDeleting) {
                                    setShowConfirmPopup(false);
                                }
                            }}
                            loading={isBulkDeleting}
                            disabled={isBulkDeleting}
                        />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Posts;